import type {
  AttributeBoundary,
  GridType,
  MapHeader,
  Nullable,
  PaletteBody,
  Property,
  Range2D,
  ScaleType,
  UpdateValue,
} from '@library/types'
import {
  convertGlobalGeologicalCoordinatesToLocal,
  convertGlobalGeologicalMeasureToLocal,
  requestRedraw,
} from '@library/utils'
import { isDefined } from 'common/helpers'
import { useEffect, useRef } from 'react'

import { GridRender } from '../GridRender'

const getCoorinates = (mapHeader: MapHeader, origin: Range2D): Range2D => {
  const size = convertGlobalGeologicalMeasureToLocal(origin, {
    width: mapHeader.columns * mapHeader.stepX,
    height: mapHeader.rows * mapHeader.stepY,
  })
  const position = convertGlobalGeologicalCoordinatesToLocal(origin, {
    x: mapHeader.offsetX,
    y: mapHeader.offsetY,
  })
  return { ...position, ...size }
}

export const useShader = (
  containerElement: Nullable<HTMLCanvasElement>,
  programs: Property<Record<string, WebGLProgram>>,
  scale: ScaleType,
  origin: Range2D,
  mapHeader: MapHeader | undefined,
  mapBody: GridType['body'],
  palette: PaletteBody,
  attributeBoundary: AttributeBoundary | undefined,
  baseIsoline: number,
  step: number,
  minorLineWidth: number,
  majorLineWidth: number,
  majorGridFrequency: number,
  showIsolines: boolean | undefined,
  setUpdate: UpdateValue<number>,
) => {
  const gridRenderRef = useRef<Nullable<GridRender>>(null)

  const dispose = () => {
    gridRenderRef.current?.[Symbol.dispose]()
    gridRenderRef.current = null
    requestRedraw(setUpdate)
  }

  useEffect(() => {
    if (gridRenderRef.current || !containerElement || !mapHeader) return dispose

    gridRenderRef.current = new GridRender(
      containerElement,
      programs,
      scale,
      getCoorinates(mapHeader, origin),
      mapHeader.nullValue,
      mapHeader.rotationAngle,
      mapHeader.rows,
      mapBody,
      baseIsoline,
      step,
      majorGridFrequency,
      majorLineWidth,
      minorLineWidth,
      [mapHeader.minZ, mapHeader.maxZ],
      palette,
      showIsolines,
    )
    requestRedraw(setUpdate)

    return dispose
  }, [containerElement, setUpdate])

  useEffect(() => {
    if (!gridRenderRef.current || !mapHeader) return

    gridRenderRef.current.setCoordinates(getCoorinates(mapHeader, origin))
    requestRedraw(setUpdate)
  }, [mapHeader, origin.x, origin.y, origin.width, origin.height, setUpdate])

  useEffect(() => {
    if (!gridRenderRef.current) return

    gridRenderRef.current.setZoomState(scale)
    requestRedraw(setUpdate)
  }, [scale, setUpdate])

  useEffect(() => {
    if (!gridRenderRef.current || !mapHeader) return

    gridRenderRef.current.setImageData(mapBody, mapHeader.rows)
  }, [mapBody, mapHeader])

  useEffect(() => {
    if (!gridRenderRef.current) return

    //todo: выделить в конфигурационный тип настройки карты
    gridRenderRef.current
      .setBaseline(baseIsoline)
      .setIsolineStep(step)
      .setMinorIsolineWidth(minorLineWidth)
      .setMajorIsolineWidth(majorLineWidth)
      .setMajorIsoline(majorGridFrequency)
    requestRedraw(setUpdate)
  }, [baseIsoline, step, minorLineWidth, majorLineWidth, majorGridFrequency, setUpdate])

  useEffect(() => {
    if (!gridRenderRef.current || !isDefined(showIsolines)) return

    gridRenderRef.current.setShowIsolines(showIsolines)
    requestRedraw(setUpdate)
  }, [showIsolines])

  useEffect(() => {
    if (!gridRenderRef.current) return

    gridRenderRef.current.setPaletteData(palette)
    requestRedraw(setUpdate)
  }, [palette])

  useEffect(() => {
    if (!gridRenderRef.current || !attributeBoundary) return

    gridRenderRef.current.setRange([attributeBoundary.start, attributeBoundary.stop])
    requestRedraw(setUpdate)
  }, [attributeBoundary])

  return gridRenderRef.current
}
